(function ryvexTheme(globalScope) {
  "use strict";

  const BIKE_KEY = "ryvex-bike";
  const THEME_KEY = "ryvex-theme";
  const UNIVERSAL = new Set(["", "all", "universal", "*"]);

  function normalizeFits(value) {
    if (Array.isArray(value)) value = value.join(",");
    const codes = String(value ?? "")
      .split(",")
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean);
    return codes.length === 0 || codes.some((code) => UNIVERSAL.has(code))
      ? []
      : [...new Set(codes)];
  }

  function fitmentVerdict(fits, selectedBike) {
    if (!selectedBike) return "unknown";
    const codes = normalizeFits(fits);
    return codes.length === 0 || codes.includes(selectedBike)
      ? "fits"
      : "does-not-fit";
  }

  function parseBikeData(documentRef) {
    const node = documentRef?.getElementById("RyvexBikeData");
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function bikeLabel(bikes, code) {
    const bike = bikes.find((item) => item.code === code);
    if (!bike) return "";
        return bike.label.toLowerCase().startsWith(bike.brand.toLowerCase()) ? bike.label : `${bike.brand} ${bike.label}`;
  }

  function readStorage(storage, key) {
    try {
      return storage?.getItem(key) || "";
    } catch {
      return "";
    }
  }

  function writeStorage(storage, key, value) {
    try {
      if (value) storage?.setItem(key, value);
      else storage?.removeItem(key);
    } catch {
      /* Storage can be unavailable in privacy modes. */
    }
  }

  function focusable(dialog) {
    return [
      ...dialog.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ),
    ].filter(
      (node) => !node.hidden && node.getAttribute("aria-hidden") !== "true",
    );
  }

  function trapFocus(event, dialog) {
    if (event.key !== "Tab") return;
    const nodes = focusable(dialog);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function money(cents, currency = "USD") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format((Number(cents) || 0) / 100);
  }

  const api = { normalizeFits, fitmentVerdict, bikeLabel, money };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (typeof globalScope !== "undefined") globalScope.RyvexFitment = api;

  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof HTMLElement === "undefined"
  )
    return;

  const bikes = parseBikeData(document);

  function selectedBike() {
    const code = readStorage(window.localStorage, BIKE_KEY);
    return bikes.some((bike) => bike.code === code) ? code : "";
  }

  function setVerdictNode(node, verdict) {
    node.dataset.state = verdict;
    node.textContent =
      verdict === "fits"
        ? "FITS"
        : verdict === "does-not-fit"
          ? "DOESN'T FIT"
          : "SELECT YOUR BIKE";
    node.setAttribute("aria-label", node.textContent.toLowerCase());
  }

  function refreshFitment(root = document) {
    const code = selectedBike();
    const label = bikeLabel(bikes, code) || "Select your bike";
    root.querySelectorAll("[data-selected-bike-label]").forEach((node) => {
      if (
        node instanceof HTMLInputElement ||
        node instanceof HTMLTextAreaElement
      )
        node.value = label === "Select your bike" ? "" : label;
      else node.textContent = label;
    });
    root
      .querySelectorAll('input[name="properties[Selected bike]"]')
      .forEach((input) => {
        input.value = label === "Select your bike" ? "" : label;
      });

    const filterEnabled = [
      ...document.querySelectorAll("[data-fitment-filter-toggle]"),
    ].some((input) => input.checked);
    root.querySelectorAll("[data-fits]").forEach((container) => {
      const verdict = fitmentVerdict(container.dataset.fits, code);
      container
        .querySelectorAll("[data-fitment-verdict]")
        .forEach((node) => setVerdictNode(node, verdict));
      if (container.matches("[data-product-card]"))
        container.hidden = filterEnabled && verdict !== "fits";
    });

    document.querySelectorAll("[data-fitment-empty]").forEach((node) => {
      const grid = node.closest("[data-product-grid]") || document;
      const visible = [...grid.querySelectorAll("[data-product-card]")].some(
        (card) => !card.hidden,
      );
      node.hidden = visible || !filterEnabled;
    });
  }

  class FitmentPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      this.dialog = this.querySelector("dialog");
      this.brand = this.querySelector("[data-bike-brand]");
      this.model = this.querySelector("[data-bike-model]");
      this.opener = null;
      const brands = [...new Set(bikes.map((bike) => bike.brand))];
      this.brand.replaceChildren(new Option("Choose a make", ""));
      brands.forEach((brand) => this.brand.add(new Option(brand, brand)));
      this.model.replaceChildren(new Option("Choose a model", ""));
      this.model.disabled = true;

      this.brand.addEventListener("change", () => {
        const options = bikes.filter((bike) => bike.brand === this.brand.value);
        this.model.replaceChildren(new Option("Choose a model", ""));
        options.forEach((bike) =>
          this.model.add(
            new Option(
              `${bike.label}${bike.aka ? ` — ${bike.aka}` : ""}`,
              bike.code,
            ),
          ),
        );
        this.model.disabled = !this.brand.value;
      });
      this.model.addEventListener("change", () => {
        if (!this.model.value) return;
        writeStorage(window.localStorage, BIKE_KEY, this.model.value);
        this.close();
        refreshFitment();
        document.dispatchEvent(
          new CustomEvent("ryvex:bike-change", {
            detail: { code: this.model.value },
          }),
        );
      });
      this.querySelector("[data-fitment-clear]")?.addEventListener(
        "click",
        () => {
          writeStorage(window.localStorage, BIKE_KEY, "");
          this.brand.value = "";
          this.model.replaceChildren(new Option("Choose a model", ""));
          this.model.disabled = true;
          this.close();
          refreshFitment();
        },
      );
      this.querySelectorAll("[data-dialog-close]").forEach((button) =>
        button.addEventListener("click", () => this.close()),
      );
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener("keydown", (event) => {
        if (event.key === "Escape") this.close();
        trapFocus(event, this.dialog);
      });
      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-fitment-open]");
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      });
      refreshFitment();
    }

    open(trigger) {
      this.opener = trigger;
      const current = selectedBike();
      const bike = bikes.find((item) => item.code === current);
      if (bike) {
        this.brand.value = bike.brand;
        this.brand.dispatchEvent(new Event("change"));
        this.model.value = bike.code;
      }
      this.dialog.showModal();
      this.brand.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      this.opener?.focus();
    }
  }

  class FitmentFilter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      const input = this.querySelector("[data-fitment-filter-toggle]");
      if (input) {
        input.checked = false;
        input.addEventListener("change", () => refreshFitment());
      }
    }
  }

  class ThemeToggle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      const saved = readStorage(window.localStorage, THEME_KEY);
      if (saved === "light" || saved === "dark")
        document.documentElement.dataset.theme = saved;
      this.querySelector("button")?.addEventListener("click", () => {
        const current = document.documentElement.dataset.theme;
        const inferredDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        const next =
          current === "dark" || (!current && inferredDark) ? "light" : "dark";
        document.documentElement.dataset.theme = next;
        writeStorage(window.localStorage, THEME_KEY, next);
      });
    }
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      this.dialog = this.querySelector("dialog");
      this.opener = null;
      document.addEventListener("click", (event) => {
        const open = event.target.closest("[data-cart-open]");
        if (open) {
          event.preventDefault();
          this.open(open);
        }
        const close = event.target.closest("[data-cart-close]");
        if (close && this.contains(close)) this.close();
        const change = event.target.closest("[data-cart-change]");
        if (change && this.contains(change))
          this.change(change.dataset.key, Number(change.dataset.quantity));
      });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener("keydown", (event) => {
        if (event.key === "Escape") this.close();
        trapFocus(event, this.dialog);
      });
    }

    async request(url, options) {
      const response = await fetch(url, {
        ...options,
        headers: { Accept: "application/json", ...(options?.headers || {}) },
      });
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.description || payload.message || "Cart request failed",
        );
      return payload;
    }

    async add(form) {
      this.setStatus("Adding…");
      try {
        const addedItem = await this.request(
          `${window.Shopify?.routes?.root || "/"}cart/add.js`,
          { method: "POST", body: new FormData(form) },
        );
        document.dispatchEvent(
          new CustomEvent("ryvex:cart-add", {
            detail: {
              bike: selectedBike(),
              quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
              variantId: String(addedItem?.variant_id || addedItem?.id || ""),
            },
          }),
        );
        await this.open(form.querySelector('[type="submit"]'));
      } catch (error) {
        this.setStatus(error.message, true);
      }
    }

    async change(key, quantity) {
      try {
        await this.request(
          `${window.Shopify?.routes?.root || "/"}cart/change.js`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: key, quantity }),
          },
        );
        await this.refresh();
      } catch (error) {
        this.setStatus(error.message, true);
      }
    }

    async refresh() {
      const cart = await this.request(
        `${window.Shopify?.routes?.root || "/"}cart.js`,
      );
      const list = this.querySelector("[data-cart-items]");
      const empty = this.querySelector("[data-cart-empty]");
      list.replaceChildren();
      empty.hidden = cart.item_count > 0;
      cart.items.forEach((item) => {
        const row = document.createElement("article");
        row.className = "cart-drawer__item";
        const info = document.createElement("div");
        const title = document.createElement("a");
        title.href = item.url;
        title.textContent = item.product_title;
        const meta = document.createElement("p");
        meta.className = "text-muted text-small";
        meta.textContent = `${item.variant_title && item.variant_title !== "Default Title" ? `${item.variant_title} · ` : ""}${money(item.final_line_price, cart.currency)}`;
        const controls = document.createElement("div");
        controls.className = "quantity-row";
        const minus = document.createElement("button");
        minus.type = "button";
        minus.dataset.cartChange = "";
        minus.dataset.key = item.key;
        minus.dataset.quantity = Math.max(0, item.quantity - 1);
        minus.setAttribute("aria-label", `Decrease ${item.product_title}`);
        minus.textContent = "−";
        const count = document.createElement("span");
        count.textContent = String(item.quantity);
        const plus = document.createElement("button");
        plus.type = "button";
        plus.dataset.cartChange = "";
        plus.dataset.key = item.key;
        plus.dataset.quantity = item.quantity + 1;
        plus.setAttribute("aria-label", `Increase ${item.product_title}`);
        plus.textContent = "+";
        controls.append(minus, count, plus);
        info.append(title, meta, controls);
        row.append(info);
        list.append(row);
      });
      this.querySelector("[data-cart-count]").textContent = String(
        cart.item_count,
      );
      document.querySelectorAll("[data-site-cart-count]").forEach((node) => {
        node.textContent = String(cart.item_count);
      });
      this.querySelector("[data-cart-total]").textContent = money(
        cart.total_price,
        cart.currency,
      );
      this.setStatus("");
    }

    async open(trigger) {
      this.opener = trigger;
      if (!this.dialog.open) this.dialog.showModal();
      await this.refresh();
      this.querySelector("[data-cart-close]")?.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      this.opener?.focus();
    }

    setStatus(message, isError = false) {
      const node = this.querySelector("[data-cart-status]");
      node.textContent = message;
      node.dataset.error = String(isError);
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      const form = this.querySelector("form");
      this.variantSelect = form?.querySelector('select[name="id"]');
      this.optionSelects = [
        ...(form?.querySelectorAll("[data-variant-option]") || []),
      ];
      this.submitButton = form?.querySelector('[type="submit"]');
      this.sourcingApproved =
        this.submitButton?.dataset.sourcingApproved === "true";
      this.consent = form?.querySelector("[data-product-consent]");
      this.productInfo = this.closest(".product-info");
      try {
        this.variants = JSON.parse(
          this.querySelector("[data-product-variants]")?.textContent || "[]",
        );
      } catch {
        this.variants = [];
      }
      this.optionSelects.forEach((select) =>
        select.addEventListener("change", () => this.updateVariantFromOptions()),
      );
      this.consent?.addEventListener("change", () => this.syncSubmit());
      form?.addEventListener("submit", (event) => {
        if (!form.reportValidity()) {
          event.preventDefault();
          return;
        }
        if (!window.fetch || !document.querySelector("cart-drawer")) return;
        event.preventDefault();
        document.querySelector("cart-drawer").add(form);
      });
      this.syncSubmit();
      refreshFitment(this);
    }

    syncSubmit() {
      if (!this.submitButton) return;
      const available =
        this.sourcingApproved &&
        this.submitButton.dataset.available !== "false";
      const accepted = !this.consent || this.consent.checked;
      const configured =
        this.optionSelects.length === 0 ||
        this.optionSelects.every((select) => Boolean(select.value));
                  this.submitButton.disabled = !available || !accepted || !configured;
                  const acceleratedCheckout = this.submitButton.closest('form')?.querySelector('[data-dynamic-checkout]');
                  if (acceleratedCheckout) acceleratedCheckout.hidden = !available;

      if (available) {
        this.submitButton.textContent = "Add to cart";
      }
    }

    updateVariantFromOptions() {
      if (!this.variantSelect) return;
      const selectedOptions = this.optionSelects.map((select) => select.value);
      if (selectedOptions.some((value) => !value)) {
        this.variantSelect.value = "";
        this.syncSubmit();
        return;
      }
      const variant = this.variants.find(
        (item) =>
          item.options?.length === selectedOptions.length &&
          item.options.every((value, index) => value === selectedOptions[index]),
      );
      this.variantSelect.value = variant ? String(variant.id) : "";
      this.updateVariant();
    }

    updateVariant() {
      const variant = this.variants.find(
        (item) => String(item.id) === this.variantSelect?.value,
      );
      if (!variant) return;
      const price = this.productInfo?.querySelector("[data-product-price]");
      if (price) {
        const row = document.createElement("div");
        row.className = "price";
        const current = document.createElement("span");
        current.className = "price__current";
        current.textContent = money(
          variant.price,
          window.Shopify?.currency?.active || "USD",
        );
        row.append(current);
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          const compare = document.createElement("s");
          compare.className = "price__compare";
          compare.textContent = money(
            variant.compare_at_price,
            window.Shopify?.currency?.active || "USD",
          );
          const saving = document.createElement("span");
          saving.className = "sale-chip";
          saving.textContent = `SAVE ${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
          row.append(compare, saving);
        }
        price.replaceChildren(row);
      }
      const stock = this.productInfo?.querySelector("[data-product-stock]");
      if (stock) {
        stock.textContent = !this.sourcingApproved
          ? "Draft sourcing review"
          : variant.available
            ? "● In stock"
            : "○ Sold out";
      }
      if (this.submitButton) {
        this.submitButton.dataset.available = String(variant.available);
        if (!this.sourcingApproved)
          this.submitButton.textContent = "Sourcing review";
        else if (!variant.available) this.submitButton.textContent = "Sold out";
        this.syncSubmit();
      }
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }
  }

  document.querySelectorAll("[data-legal-gate]").forEach((gate) => {
    const checkbox = gate.querySelector("[data-legal-consent]");
    const scope = gate.closest("form") || gate.parentElement;
    const submit = scope?.querySelector("[data-legal-submit]");
    if (!checkbox || !submit) return;
    const sync = () => {
      submit.disabled = !checkbox.checked;
      submit.textContent = checkbox.checked
        ? "Checkout"
        : "Accept policies to checkout";
    };
    checkbox.addEventListener("change", sync);
    sync();
  });

  class ProductRecommender extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      this.dialog = this.querySelector("#ProductFinder");
      this.intro = this.querySelector("[data-finder-intro]");
      this.survey = this.querySelector("[data-finder-survey]");
      this.source = this.querySelector("[data-recommendation-source]");
      this.results = this.querySelector("[data-finder-results]");
      this.summary = this.querySelector("[data-finder-summary]");
      this.bike = "";
      this.bikeLabel = "";
      this.opener = null;
      this.addEventListener("click", (event) => this.onClick(event));
      this.dialog?.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          this.dialog.close();
          return;
        }
        trapFocus(event, this.dialog);
      });
      this.dialog?.addEventListener("close", () => {
        window.clearTimeout(this.introTimer);
        this.opener?.focus?.();
      });
      if (!readStorage(window.sessionStorage, "ryvex-finder-seen-v1")) {
        this.autoTimer = window.setTimeout(() => this.open(true), 800);
      }
    }

    onClick(event) {
      const opener = event.target.closest("[data-recommender-open]");
      if (opener) {
        this.opener = opener;
        this.open(false);
        return;
      }
      if (event.target.closest("[data-recommender-close]")) {
        this.dialog?.close();
        return;
      }
      const bike = event.target.closest("[data-finder-bike]");
      if (bike) {
        this.bike = bike.dataset.finderBike;
        this.bikeLabel = bike.dataset.finderBikeLabel;
        this.showStep("goal");
        return;
      }
      if (
        event.target.closest("[data-finder-back]") ||
        event.target.closest("[data-finder-reset]")
      ) {
        this.showStep("bike");
        return;
      }
      const goal = event.target.closest("[data-finder-goal]");
      if (goal)
        this.showResults(goal.dataset.finderGoal, goal.dataset.finderGoalLabel);
    }

    open(animateIntro) {
      if (
        !this.dialog ||
        this.dialog.open ||
        document.querySelector("dialog[open]")
      )
        return;
      writeStorage(window.sessionStorage, "ryvex-finder-seen-v1", "1");
      this.dialog.showModal();
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (animateIntro && !reduceMotion) {
        this.intro.hidden = false;
        this.survey.hidden = true;
        this.dialog.dataset.stage = "intro";
        this.introTimer = window.setTimeout(() => this.showSurvey(), 1850);
      } else {
        this.showSurvey();
      }
    }

    showSurvey() {
      if (!this.dialog?.open) return;
      this.intro.hidden = true;
      this.survey.hidden = false;
      this.dialog.dataset.stage = "survey";
      this.showStep("bike");
    }

    showStep(name) {
      this.querySelectorAll("[data-finder-step]").forEach((step) => {
        step.hidden = step.dataset.finderStep !== name;
      });
      const step = this.querySelector(`[data-finder-step="${name}"]`);
      window.requestAnimationFrame(() =>
        step?.querySelector("button")?.focus(),
      );
    }

    showResults(goal, goalLabel) {
      const allCards = [
        ...this.source.querySelectorAll("[data-recommendation-card]"),
        ];
        const bikeMatches = (card) => {
        const bikesForCard = card.dataset.bikes.split(",");
        return this.bike === "any"
        ? bikesForCard.includes("all")
        : bikesForCard.includes("all") || bikesForCard.includes(this.bike);
        };
        let candidates = allCards
        .filter((card) => card.dataset.goal === goal && bikeMatches(card))
        .slice(0, 3);
        let usedFallback = false;
        if (candidates.length === 0) {
        candidates = allCards.filter(bikeMatches).slice(0, 3);
        usedFallback = true;
        }
        this.results.replaceChildren(
        ...candidates.map((card) => card.cloneNode(true)),
        );
        this.summary.textContent = usedFallback
        ? `${this.bikeLabel} · ${goalLabel}. Nothing dedicated yet — here's what fits your bike.`
        : `${this.bikeLabel} · ${goalLabel}. Three focused places to start.`;
      document.dispatchEvent(
        new CustomEvent("ryvex:finder-results", {
          detail: {
            bike: this.bike,
            goal,
            resultCount: candidates.length,
          },
        }),
      );
      this.showStep("results");
    }
  }

  const elements = {
    "fitment-picker": FitmentPicker,
    "fitment-filter": FitmentFilter,
    "theme-toggle": ThemeToggle,
    "cart-drawer": CartDrawer,
    "product-form": ProductForm,
    "product-recommender": ProductRecommender,
  };
  Object.entries(elements).forEach(([name, constructor]) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  });

  document.addEventListener("shopify:section:load", (event) =>
    refreshFitment(event.target),
  );
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => refreshFitment());
  else refreshFitment();
})(typeof window !== "undefined" ? window : globalThis);
